import { useCallback, useEffect, useId, useState } from "react";
import { Link, Navigate, useInRouterContext, useParams } from "react-router-dom";
import { Edit2, Loader2, Plus, Trash2 } from "lucide-react";
import { businessApi } from "../../../services/api";
import HRPageShell from "./components/HRPageShell";
import ShopSelect from "../../../components/ShopSelect";
import {
  Button, ConfirmDialog, Dialog, DisclosureLink, ErrorState, Field, IconButton, Input, Notice, RouteTabs, Select, SkeletonRows, Surface,
  Table, TableContainer, TBody, Td, Textarea, Th, THead, Tr, cn, focusRing, plainError,
} from "../../../components/ui";
import DatePickerInput from "../../../components/DatePickerInput";
import { ruleFacts, rulePeriodText } from "./statutoryView";

// Report 55 (OD-34): three tabs, route-backed. Government deductions are read only and worked out by Apparify; a
// business can only set up its own deductions.
export const DEDUCTION_TABS = [
  { key: "government", label: "Government deductions" },
  { key: "types", label: "Business deduction types" },
  { key: "employees", label: "Employee deductions & loans" },
];
const BASE = "/business/hr/deductions";

// SSS, PhilHealth, Pag-IBIG and withholding tax are managed by Apparify, so "government" and "tax" are not choices
// for an ordinary deduction. Older rows in those categories still show their label.
const RESERVED_CATEGORY_LABELS = { government: "Government", tax: "Tax", attendance: "Attendance (no longer offered)" };
export const STATUTORY_DEDUCTION_NOTE = "SSS, PhilHealth, Pag-IBIG and withholding tax are managed by Apparify and "
  + "cannot be set up as ordinary deductions.";

// Report 55 (OD-35, APP-HR-128): "attendance" is no longer offered. Payroll already takes unpaid absences, leave and
// suspension out of basic pay, so a second attendance deduction would take them twice. Old attendance types stay
// readable with their label.
export const CATEGORY_OPTIONS = [
  { value: "loan", label: "Loan or advance", hint: "Money the business lent the employee, paid back from pay." },
  { value: "company", label: "Company deduction", hint: "A business charge such as uniforms or canteen purchases." },
  { value: "benefit", label: "Benefit contribution", hint: "The employee's share of an optional benefit plan." },
  { value: "other", label: "Other", hint: "Anything that does not fit the kinds above." },
];

const FREQUENCY_OPTIONS = [
  { value: "per_payroll", label: "Every payroll", hint: "Taken on every payslip." },
  { value: "monthly", label: "Once a month", hint: "Taken once, on the last payroll of the month." },
  { value: "semi_monthly", label: "Twice a month", hint: "Only for employees on a twice-a-month pay schedule." },
];

export const BASIS_OPTIONS = [
  { value: "EMPLOYEE_CONSENT", label: "The employee agreed in writing", hint: "Keep the signed agreement in the employee's records." },
  { value: "REQUIRED_BY_LAW", label: "A law or order requires it", hint: "Name the law, order or court decision." },
  { value: "OTHER", label: "Another basis", hint: "Describe it. Deductions from wages are allowed only with consent or legal authority." },
];

const ASSIGNMENT_STATUS_OPTIONS = [
  { value: "active", label: "Active", hint: "This deduction currently applies to the employee." },
  { value: "inactive", label: "Inactive", hint: "The assignment stays on record but is not currently applied." },
  { value: "completed", label: "Completed", hint: "Use for finished one-off or fully paid loan assignments." },
  { value: "cancelled", label: "Cancelled", hint: "Use when the assignment should no longer be used." },
];

const ASSIGNMENT_STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  inactive: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  completed: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
  cancelled: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

const inputCls = "w-full rounded-lg border px-3 py-2 text-sm dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-100";
const helperCls = "mt-1 text-xs text-neutral-500 dark:text-neutral-400";

const formatTokenLabel = (value) => String(value || "").replaceAll("_", " ");

const formatPesoInputFromCents = (cents) => {
  if (cents == null || Number.isNaN(Number(cents))) return "0.00";
  return (Number(cents) / 100).toFixed(2);
};

const parsePesoInputToCents = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
};

const formatCurrency = (cents) => {
  if (cents == null) return "-";
  return `PHP ${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
};

/** The kind of a deduction as a plain word (status and kind are never colour alone). */
function CategoryBadge({ category }) {
  return <span className="text-body-sm text-secondary">{kindLabel(category)}</span>;
}

const frequencyLabel = (value) => FREQUENCY_OPTIONS.find((option) => option.value === value)?.label || formatTokenLabel(value);
const kindLabel = (value) => CATEGORY_OPTIONS.find((option) => option.value === value)?.label || RESERVED_CATEGORY_LABELS[value] || value;
// Report 57: an SSS or Pag-IBIG loan is repaid to a government agency, not to the business. The server says which
// (government_agency); internal codes are never shown.
const AGENCY_WORD = { SSS: "SSS", PAGIBIG: "Pag-IBIG" };
const typeKindLabel = (type) => (type?.government_agency ? `Government loan repayment (${AGENCY_WORD[type.government_agency] || type.government_agency})` : kindLabel(type?.category));
export const GOVERNMENT_LOAN_NOTE = "Taking a government loan repayment from pay does not by itself pay the agency. Finance remits it separately and keeps the proof.";

// Editing these on a type changes how it is deducted for employees given it later (OD-4); name and description do not.
const PAYROLL_TYPE_FIELDS = [
  ["calculation_type", "How the amount is set", (v) => (v === "percentage" ? "Percentage of gross pay" : "Fixed amount")],
  ["default_amount_cents", "Usual amount", (v) => formatCurrency(v)],
  ["default_rate", "Usual rate", (v) => (v == null || v === "" ? "-" : `${v}%`)],
  ["frequency", "When it is taken", (v) => frequencyLabel(v)],
  ["affects_payroll", "Taken by payroll", (v) => (Number(v) ? "Yes" : "No (record only)")],
];

const norm = (value) => (value === null || value === undefined || value === "" ? "" : Number.isFinite(Number(value)) ? String(Number(value)) : String(value));

export function payrollTypeChanges(before, after) {
  if (!before?.id) return [];
  return PAYROLL_TYPE_FIELDS
    .filter(([key]) => after[key] !== undefined && norm(before[key]) !== norm(after[key]))
    .map(([key, label, show]) => ({ label, value: `${show(before[key])} → ${show(after[key])}` }));
}

/** Field-level checks before anything is sent; the server checks the same and more. */
export function deductionTypeErrors(form, { editing = false } = {}) {
  const errors = {};
  const name = String(form.name || "").trim();
  if (!name) errors.name = "Name the deduction.";
  else if (name.length > 100) errors.name = "Keep the name under 100 characters.";
  if (!editing && !CATEGORY_OPTIONS.some((option) => option.value === form.category)) errors.category = "Choose what kind of deduction this is.";
  if (form.calculation_type === "percentage") {
    const rate = Number(form.default_rate);
    if (form.default_rate === "" || !Number.isFinite(rate) || rate <= 0 || rate > 100) errors.default_rate = "Enter a rate above 0% and up to 100%.";
  } else {
    const raw = String(form.default_amount ?? "").trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) errors.default_amount = "Enter an amount like 250.00.";
  }
  if (!BASIS_OPTIONS.some((option) => option.value === form.basis_kind)) errors.basis_kind = "Say why this may be taken from wages.";
  const note = String(form.basis_note || "").trim();
  if (form.basis_kind && form.basis_kind !== "EMPLOYEE_CONSENT" && note.length < 10) {
    errors.basis_note = form.basis_kind === "REQUIRED_BY_LAW" ? "Name the law or order (at least 10 characters)." : "Describe the basis (at least 10 characters).";
  }
  if (note.length > 500) errors.basis_note = "Keep this under 500 characters.";
  if (String(form.description || "").length > 255) errors.description = "Keep the description under 255 characters.";
  return errors;
}

const emptyType = {
  name: "", description: "", category: "", calculation_type: "fixed", default_amount: "0.00", default_rate: "",
  frequency: "per_payroll", supports_balance_tracking: 0, is_mandatory: 0, record_only: 0, basis_kind: "", basis_note: "",
};

function TypeFormModal({ onClose, onSubmit, initial }) {
  const editing = !!initial?.id;
  const formId = useId();
  const [form, setForm] = useState(() => (initial ? {
    name: initial.name || "",
    description: initial.description || "",
    category: initial.category || "other",
    calculation_type: initial.calculation_type || "fixed",
    default_amount: formatPesoInputFromCents(initial.default_amount_cents),
    default_rate: initial.default_rate ?? initial.percentage_rate ?? "",
    frequency: initial.frequency || "per_payroll",
    supports_balance_tracking: Number(initial.supports_balance_tracking || 0),
    is_mandatory: Number(initial.is_mandatory || 0),
    record_only: Number(initial.affects_payroll ?? 1) ? 0 : 1,
    basis_kind: initial.deduction_basis_kind || "",
    basis_note: initial.deduction_basis_note || "",
  } : emptyType));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");

  const set = (key) => (event) => {
    const value = event?.target ? (event.target.type === "checkbox" ? (event.target.checked ? 1 : 0) : event.target.value) : event;
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setFailure("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = deductionTypeErrors(form, { editing });
    setErrors(found);
    const first = ["name", "category", "default_amount", "default_rate", "basis_kind", "basis_note", "description"].find((key) => found[key]);
    if (first) { document.getElementById(`${formId}-${first}`)?.focus(); return; }
    setSaving(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        description: form.description.trim() || null,
        ...(editing ? {} : { category: form.category }),
        calculation_type: form.calculation_type,
        default_amount_cents: form.calculation_type === "fixed" ? parsePesoInputToCents(form.default_amount) ?? 0 : 0,
        default_rate: form.calculation_type === "percentage" ? Number(form.default_rate) : null,
        frequency: form.frequency,
        supports_balance_tracking: Number(!!form.supports_balance_tracking),
        is_mandatory: Number(!!form.is_mandatory),
        affects_payroll: form.record_only ? 0 : 1,
        deduction_basis_kind: form.basis_kind,
        deduction_basis_note: form.basis_note.trim() || null,
      });
    } catch (err) {
      setFailure(plainError(err, "The deduction type was not saved. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const basis = BASIS_OPTIONS.find((option) => option.value === form.basis_kind);
  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={editing ? `Edit ${initial.name}` : "New deduction type"}
      description="A deduction the business can give employees. It takes nothing from anyone until an employee is given it."
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving}>{editing ? "Save changes" : "Create deduction type"}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required id={`${formId}-name`} error={errors.name}>
            <Input value={form.name} onChange={set("name")} maxLength={100} autoComplete="off" placeholder="Canteen loan" disabled={saving} />
          </Field>
          {editing ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-label text-secondary">Kind</span>
              <span className="flex h-control items-center text-body text-foreground">{typeKindLabel(initial)}</span>
            </div>
          ) : (
            <Field label="What kind of deduction" required id={`${formId}-category`} error={errors.category}
              hint={CATEGORY_OPTIONS.find((option) => option.value === form.category)?.hint || STATUTORY_DEDUCTION_NOTE}>
              <Select value={form.category} onChange={set("category")} disabled={saving}>
                <option value="">Choose…</option>
                {CATEGORY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            </Field>
          )}
        </div>

        <fieldset className="grid gap-4 sm:grid-cols-3">
          <legend className="sr-only">How much is taken</legend>
          <Field label="How the amount is set">
            <Select value={form.calculation_type} onChange={set("calculation_type")} disabled={saving}>
              <option value="fixed">A fixed amount</option>
              <option value="percentage">A percentage of gross pay</option>
            </Select>
          </Field>
          {form.calculation_type === "percentage" ? (
            <Field label="Usual rate (%)" required id={`${formId}-default_rate`} error={errors.default_rate} hint="Of the payslip's gross pay. Prefills a new employee deduction.">
              <Input inputMode="decimal" autoComplete="off" value={form.default_rate} onChange={set("default_rate")} disabled={saving} className="tabular-nums" />
            </Field>
          ) : (
            <Field label="Usual amount (₱)" required id={`${formId}-default_amount`} error={errors.default_amount} hint="Prefills a new employee deduction; each employee keeps their own amount.">
              <Input inputMode="decimal" autoComplete="off" value={form.default_amount} onChange={set("default_amount")} disabled={saving} className="tabular-nums" />
            </Field>
          )}
          <Field label="When it is taken" hint={FREQUENCY_OPTIONS.find((option) => option.value === form.frequency)?.hint}>
            <Select value={form.frequency} onChange={set("frequency")} disabled={saving}>
              {FREQUENCY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </Field>
        </fieldset>

        <label className="flex items-start gap-3 text-body-sm">
          <input type="checkbox" checked={!!form.supports_balance_tracking} onChange={set("supports_balance_tracking")} disabled={saving} className="mt-0.5 h-4 w-4 max-md:h-5 max-md:w-5" />
          <span>
            <span className="block font-medium text-foreground">Paid down in installments (a loan or advance)</span>
            <span className="text-meta text-secondary">Each employee deduction then keeps a remaining balance that goes down only when a salary is paid.</span>
          </span>
        </label>

        <fieldset className="space-y-3 border-t border-subtle pt-4">
          <legend className="text-label text-foreground">Why this may be taken from wages</legend>
          <Field label="Basis" required id={`${formId}-basis_kind`} error={errors.basis_kind} hint={basis?.hint || "A deduction from wages needs the employee's consent or the authority of a law."}>
            <Select value={form.basis_kind} onChange={set("basis_kind")} disabled={saving}>
              <option value="">Choose…</option>
              {BASIS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
          </Field>
          <Field label={form.basis_kind === "REQUIRED_BY_LAW" ? "Which law or order" : form.basis_kind === "OTHER" ? "The basis" : "Note (optional)"}
            required={["REQUIRED_BY_LAW", "OTHER"].includes(form.basis_kind)} id={`${formId}-basis_note`} error={errors.basis_note}
            hint="Recorded with the type. It is your statement, not a legal approval.">
            <Input value={form.basis_note} onChange={set("basis_note")} maxLength={500} autoComplete="off" disabled={saving} />
          </Field>
        </fieldset>

        <Field label="Description (optional)" id={`${formId}-description`} error={errors.description}>
          <Textarea rows={2} value={form.description} onChange={set("description")} maxLength={255} disabled={saving} />
        </Field>

        <DisclosureLink label="More options" contentClassName="space-y-3 pt-2">
          <label className="flex items-start gap-3 text-body-sm">
            <input type="checkbox" checked={!!form.is_mandatory} onChange={set("is_mandatory")} disabled={saving} className="mt-0.5 h-4 w-4 max-md:h-5 max-md:w-5" />
            <span>
              <span className="block font-medium text-foreground">New hires must be given this</span>
              <span className="text-meta text-secondary">Onboarding lists it until it is given. It is not applied to anyone by itself.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-body-sm">
            <input type="checkbox" checked={!!form.record_only} onChange={set("record_only")} disabled={saving} className="mt-0.5 h-4 w-4 max-md:h-5 max-md:w-5" />
            <span>
              <span className="block font-medium text-foreground">Record only — payroll does not take it</span>
              <span className="text-meta text-secondary">For something tracked outside payroll.</span>
            </span>
          </label>
        </DisclosureLink>

        {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

/** APP-HR-085: the fields an edit sends — only what changed, so a form opened earlier never writes back old values. */
export function changedAssignmentFields(initial, payload) {
  const same = (a, b) => (a === null || a === undefined || a === "" ? null : String(a)) === (b === null || b === undefined || b === "" ? null : String(b));
  const before = {
    status: initial?.status || "active",
    effective_from: initial?.effective_from ? String(initial.effective_from).slice(0, 10) : null,
    effective_until: initial?.effective_until ? String(initial.effective_until).slice(0, 10) : null,
    notes: initial?.notes || null,
    override_amount_cents: initial?.override_amount_cents ?? null,
    override_rate: initial?.override_rate ?? null,
    remaining_balance_cents: initial?.remaining_balance_cents ?? null,
    installment_amount_cents: initial?.installment_amount_cents ?? null,
  };
  return Object.fromEntries(Object.entries(payload).filter(([key, value]) => key in before && !same(before[key], value)));
}

function AssignmentModal({ open, onClose, onSubmit, deductionTypes, initial, memberLabel }) {
  const editing = !!initial?.id;
  // APP-HR-085 (OD-14): once payroll that went to Finance has taken it, its amount, installment, balance and start date are fixed.
  const used = editing && !!initial?.is_used;
  const [form, setForm] = useState({
    deduction_type_id: "",
    status: "active",
    override_amount: "",
    override_rate: "",
    remaining_balance: "",
    installment_amount: "",
    effective_from: "",
    effective_until: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      initial
        ? {
            deduction_type_id: String(initial.deduction_type_id || ""),
            status: initial.status || "active",
            override_amount: initial.override_amount_cents != null ? formatPesoInputFromCents(initial.override_amount_cents) : "",
            override_rate: initial.override_rate ?? "",
            remaining_balance: initial.remaining_balance_cents != null ? formatPesoInputFromCents(initial.remaining_balance_cents) : "",
            installment_amount: initial.installment_amount_cents != null ? formatPesoInputFromCents(initial.installment_amount_cents) : "",
            effective_from: initial.effective_from ? String(initial.effective_from).slice(0, 10) : "",
            effective_until: initial.effective_until ? String(initial.effective_until).slice(0, 10) : "",
            notes: initial.notes || "",
          }
        : {
            deduction_type_id: "",
            status: "active",
            override_amount: "",
            override_rate: "",
            remaining_balance: "",
            installment_amount: "",
            effective_from: "",
            effective_until: "",
            notes: "",
          },
    );
  }, [open, initial]);

  if (!open) return null;

  const selectedType = deductionTypes.find((type) => String(type.id) === String(form.deduction_type_id)) || null;
  const canOverride = !!selectedType?.supports_employee_override;
  const supportsBalanceTracking = !!selectedType?.supports_balance_tracking;
  const usesFixedAmount = selectedType?.calculation_type === "fixed";
  const usesPercentageRate = selectedType?.calculation_type === "percentage";
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.deduction_type_id || !form.effective_from) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        deduction_type_id: Number(form.deduction_type_id),
        status: form.status,
        effective_from: form.effective_from,
        effective_until: form.effective_until || null,
        notes: form.notes.trim() || null,
        override_amount_cents: canOverride && usesFixedAmount ? parsePesoInputToCents(form.override_amount) : null,
        override_rate: canOverride && usesPercentageRate && form.override_rate !== "" ? Number(form.override_rate) : null,
        remaining_balance_cents: supportsBalanceTracking ? parsePesoInputToCents(form.remaining_balance) : null,
        installment_amount_cents: supportsBalanceTracking ? parsePesoInputToCents(form.installment_amount) : null,
      };
      await onSubmit(payload, selectedType, editing ? changedAssignmentFields(initial, payload) : payload);
    } catch (err) {
      setError(plainError(err, "The assignment was not saved. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={editing ? "Edit this deduction" : "Assign a deduction"}
      description={`${memberLabel ? `${memberLabel}: ` : ""}whether, when and how much of this deduction payroll takes from their pay. You review it before it is saved.`}
    >
      <form className="pb-2" onSubmit={handleSubmit}>
        {used ? (
          <Notice tone="neutral" title="Its amounts are fixed" className="mt-4">
            Payroll that went to Finance has already taken this deduction, so its amount, installment, balance and start
            date cannot change. A balance goes down only when a salary carrying the installment is confirmed as paid. You
            can still pause it, set an end date, or end it once nothing withheld is waiting to be paid.
          </Notice>
        ) : null}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Deduction Type *</label>
            <ShopSelect value={form.deduction_type_id} onValueChange={(value) => setForm((current) => ({ ...current, deduction_type_id: value }))} options={[{ value: "", label: "- Select deduction type -" }, ...deductionTypes.filter((type) => type.is_active || String(type.id) === String(form.deduction_type_id)).map((type) => ({ value: type.id, label: type.government_agency ? `${type.name} (government loan)` : type.name }))]} disabled={editing} ariaLabel="Deduction Type" align="start" minPanelWidth={240} triggerClassName={`min-h-10 w-full rounded-xl px-3 py-2 text-sm ${editing ? "opacity-60" : ""}`} panelClassName="py-1" />
            <p className={helperCls}>{editing ? "Changing the template would create a different assignment. Edit the current record instead." : "Only active deduction templates can be assigned to employees."}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Assignment Status</label>
            <ShopSelect value={form.status} onValueChange={(value) => setForm((current) => ({ ...current, status: value }))} options={ASSIGNMENT_STATUS_OPTIONS} ariaLabel="Assignment Status" align="start" minPanelWidth={180} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm" panelClassName="py-1" />
            <p className={helperCls}>{ASSIGNMENT_STATUS_OPTIONS.find((option) => option.value === form.status)?.hint}</p>
          </div>
        </div>

        {selectedType && (
          <div className="mt-4 rounded-2xl border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/50">
            <div className="flex flex-wrap items-center gap-2">
                            <CategoryBadge category={selectedType.category} />
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{formatTokenLabel(selectedType.calculation_type)}</span>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{formatTokenLabel(selectedType.frequency)}</span>
            </div>
            <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-300">{selectedType.description || "No deduction template description yet."}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${selectedType.supports_balance_tracking ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"}`}>{selectedType.supports_balance_tracking ? "Balance-tracked" : "No balance tracking"}</span>
              <span className={`rounded-full px-2 py-0.5 font-medium ${selectedType.affects_payroll ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"}`}>{selectedType.affects_payroll ? "Payroll-affecting" : "Reference only"}</span>
            </div>
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Effective From *</label>
            <DatePickerInput value={form.effective_from} onValueChange={(value) => set("effective_from")({ target: { value } })} required disabled={used} title="SELECT EFFECTIVE DATE" ariaLabel="Effective from" className={`${inputCls} ${used ? "opacity-60" : ""}`} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Effective Until</label>
            <DatePickerInput value={form.effective_until} onValueChange={(value) => set("effective_until")({ target: { value } })} min={form.effective_from} title="SELECT END DATE" ariaLabel="Effective until" className={inputCls} />
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Their Amount (PHP)</label>
            <input type="number" step="0.01" min="0" value={form.override_amount} onChange={set("override_amount")} disabled={used || !selectedType || !canOverride || !usesFixedAmount} className={`${inputCls} ${used || !selectedType || !canOverride || !usesFixedAmount ? "opacity-60" : ""}`} placeholder="Optional" />
            <p className={helperCls}>{!selectedType ? "Select a deduction type first." : !usesFixedAmount ? "Percentage-based templates use a rate instead." : !canOverride ? `Set by the type: ${formatCurrency(selectedType.default_amount_cents)} is saved on this assignment.` : editing ? "Saved on this assignment. Leave as it is to keep it." : `Leave blank to use the type's default (${formatCurrency(selectedType.default_amount_cents)}). It is saved on this assignment; later changes to the type do not change it.`}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Their Rate (%)</label>
            <input type="number" step="0.01" min="0" max="100" value={form.override_rate} onChange={set("override_rate")} disabled={used || !selectedType || !canOverride || !usesPercentageRate} className={`${inputCls} ${used || !selectedType || !canOverride || !usesPercentageRate ? "opacity-60" : ""}`} placeholder="Optional" />
            <p className={helperCls}>{!selectedType ? "Select a deduction type first." : !usesPercentageRate ? "Fixed templates use an amount instead." : !canOverride ? `Set by the type: ${selectedType.default_rate ?? 0}% is saved on this assignment.` : editing ? "Saved on this assignment. Leave as it is to keep it." : `Leave blank to use the type's default (${selectedType.default_rate ?? 0}%). It is saved on this assignment; later changes to the type do not change it.`}</p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Remaining Balance (PHP)</label>
            <input type="number" step="0.01" min="0" value={form.remaining_balance} onChange={set("remaining_balance")} disabled={used || !selectedType || !supportsBalanceTracking} className={`${inputCls} ${used || !selectedType || !supportsBalanceTracking ? "opacity-60" : ""}`} placeholder="Optional" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Installment Amount (PHP)</label>
            <input type="number" step="0.01" min="0" value={form.installment_amount} onChange={set("installment_amount")} disabled={used || !selectedType || !supportsBalanceTracking} className={`${inputCls} ${used || !selectedType || !supportsBalanceTracking ? "opacity-60" : ""}`} placeholder="Optional" />
          </div>
        </div>
        <p className={helperCls}>{supportsBalanceTracking ? "The installment is taken each payroll until the balance is paid. The balance goes down only when the salary is confirmed received." : "Balance and installment tracking stays disabled unless the template is marked as balance-tracked."}</p>

        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Notes</label>
          <textarea rows={3} value={form.notes} onChange={set("notes")} maxLength={255} placeholder="Optional operational note or reference." className={inputCls} />
          <p className={helperCls}>Notes describe this employee assignment. They do not replace payroll computation rules.</p>
        </div>

        {error ? <Notice tone="danger" title="Not saved" role="alert" className="mt-4">{error}</Notice> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800 dark:text-neutral-300">Cancel</button>
          <button type="submit" disabled={saving} className="business-primary-action inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Review deduction
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function TypesTab({ deductionTypes, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const save = async (data) => {
    if (editing?.id) await businessApi.updateDeductionType(editing.id, data);
    else await businessApi.createDeductionType(data);
    setShowForm(false);
    setEditing(null);
    setReviewing(null);
    onReload();
  };

  // Creating a type, or editing only its name or description, is a C1 form save. An edit that changes how payroll
  // deducts it (C3) is reviewed first.
  const handleSubmit = async (data) => {
    const changes = payrollTypeChanges(editing, data);
    if (changes.length) {
      setReviewing({ data, changes });
      return;
    }
    await save(data);
  };

  const confirmToggle = async () => {
    await businessApi.updateDeductionType(toggling.id, { is_active: toggling.is_active ? 0 : 1 });
    setToggling(null);
    onReload();
  };
  // APP-HR-080: a deduction type nothing has used can be deleted. The server proves it again when asked.
  const confirmDelete = async () => {
    await businessApi.deleteDeductionType(deleting.id);
    setDeleting(null);
    onReload();
  };

  return (
    <>
      <Surface as="section" aria-label="Business deduction types">
        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-section-title text-foreground">Business deduction types</h2>
            <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">Deductions the business can give employees. A type takes nothing from anyone until an employee is given it. {STATUTORY_DEDUCTION_NOTE}</p>
            <p className="mt-1 max-w-[75ch] text-body-sm text-secondary">
              Lateness, leaving early and absences are not deductions: payroll works them out from attendance under the{" "}
              <AppLink to="/business/hr/settings/attendance-pay">attendance pay rules</AppLink>. {GOVERNMENT_LOAN_NOTE}
            </p>
          </div>
          <Button variant="primary" icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} className="shrink-0">New deduction type</Button>
        </div>
        {deductionTypes.length === 0 ? (
          <p className="border-t border-subtle px-4 py-6 text-body-sm text-secondary">No deduction types yet. Create one to give employees a loan, an advance or a company deduction.</p>
        ) : (
          <>
          {/* Phones: one condensed row per type (DESIGN.md › Mobile composition), never a squeezed table. */}
          <ul className="divide-y divide-subtle border-t border-subtle md:hidden" aria-label="Business deduction types">
            {deductionTypes.map((type) => {
              const basis = BASIS_OPTIONS.find((option) => option.value === type.deduction_basis_kind);
              return (
                <li key={type.id} className="space-y-1 px-4 py-3 text-body-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{type.name}</p>
                      <p className="text-meta text-secondary">{typeKindLabel(type)}</p>
                    </div>
                    <span className="flex shrink-0 items-center">
                      <IconButton icon={Edit2} label={`Edit ${type.name}`} onClick={() => { setEditing(type); setShowForm(true); }} />
                      {Number(type.never_used) === 1 ? <IconButton icon={Trash2} label={`Delete ${type.name} (never used)`} onClick={() => setDeleting(type)} /> : null}
                    </span>
                  </div>
                  <p className="text-foreground">
                    {type.calculation_type === "percentage" ? `${type.default_rate ?? 0}% of gross pay` : formatCurrency(type.default_amount_cents)}
                    <span className="text-secondary"> · {frequencyLabel(type.frequency)}{Number(type.supports_balance_tracking) ? " · paid down in installments" : ""}{Number(type.affects_payroll) ? "" : " · record only"}</span>
                  </p>
                  <p className="text-meta text-secondary">Basis: {basis ? `${basis.label}${type.deduction_basis_note ? ` — ${type.deduction_basis_note}` : ""}` : "Not recorded"}</p>
                  <button type="button" onClick={() => setToggling(type)} aria-label={`${type.is_active ? "Available" : "Inactive"}: change ${type.name}`}
                    className={cn("inline-flex min-h-touch items-center rounded-control text-body-sm hover:underline", type.is_active ? "text-foreground" : "text-secondary", focusRing)}>
                    {type.is_active ? "Available" : "Inactive"}
                  </button>
                </li>
              );
            })}
          </ul>
          <TableContainer className="hidden border-t border-subtle md:block">
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Deduction</Th>
                  <Th scope="col">How it is taken</Th>
                  <Th scope="col">Basis</Th>
                  <Th scope="col">State</Th>
                  <Th scope="col"><span className="sr-only">Actions</span></Th>
                </Tr>
              </THead>
              <TBody>
                {deductionTypes.map((type) => {
                  const basis = BASIS_OPTIONS.find((option) => option.value === type.deduction_basis_kind);
                  return (
                    <Tr key={type.id}>
                      <Td className="align-top">
                        <span className="block font-medium text-foreground">{type.name}</span>
                        <span className="block text-meta text-secondary">{typeKindLabel(type)}</span>
                        {type.description ? <span className="mt-0.5 block max-w-[48ch] whitespace-normal text-meta text-tertiary">{type.description}</span> : null}
                      </Td>
                      <Td className="align-top">
                        <span className="block text-foreground">{type.calculation_type === "percentage" ? `${type.default_rate ?? 0}% of gross pay` : formatCurrency(type.default_amount_cents)}</span>
                        <span className="block text-meta text-secondary">
                          {frequencyLabel(type.frequency)}{Number(type.supports_balance_tracking) ? " · paid down in installments" : ""}{Number(type.affects_payroll) ? "" : " · record only"}
                        </span>
                      </Td>
                      <Td className="align-top">
                        {basis ? <span className="block max-w-[32ch] whitespace-normal text-body-sm text-foreground">{basis.label}{type.deduction_basis_note ? <span className="block text-meta text-secondary">{type.deduction_basis_note}</span> : null}</span>
                          : <span className="text-meta text-tertiary">Not recorded</span>}
                      </Td>
                      <Td className="align-top">
                        <button type="button" onClick={() => setToggling(type)} aria-label={`${type.is_active ? "Available" : "Inactive"}: change ${type.name}`}
                          className={cn("rounded-control text-body-sm hover:underline", type.is_active ? "text-foreground" : "text-secondary", focusRing)}>
                          {type.is_active ? "Available" : "Inactive"}
                        </button>
                      </Td>
                      <Td className="whitespace-nowrap text-right align-top">
                        <IconButton icon={Edit2} label={`Edit ${type.name}`} onClick={() => { setEditing(type); setShowForm(true); }} />
                        {Number(type.never_used) === 1 ? <IconButton icon={Trash2} label={`Delete ${type.name} (never used)`} onClick={() => setDeleting(type)} /> : null}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Surface>

      {showForm ? <TypeFormModal onClose={() => { setShowForm(false); setEditing(null); }} onSubmit={handleSubmit} initial={editing} /> : null}
      {reviewing ? (
        <ConfirmDialog
          size="md"
          title={`Save the changes to ${editing?.name || "this deduction"}?`}
          facts={reviewing.changes}
          consequence="A new default only fills in deductions given from now on: employees who already have this deduction keep their own amount. While employees have it, whether it is fixed or a percentage, how often it is taken, whether payroll takes it and balance tracking cannot change."
          confirmLabel="Save deduction type"
          cancelLabel="Back to editing"
          failureTitle="Not saved"
          onConfirm={() => save(reviewing.data)}
          onClose={() => setReviewing(null)}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          consequence="No employee has ever been given this deduction and no payslip lists it, so it is removed for good and its code becomes free. This cannot be undone. A deduction type that has been used cannot be deleted; it is made inactive instead."
          confirmLabel="Delete deduction type"
          cancelLabel="Keep deduction type"
          tone="danger"
          failureTitle="Not deleted"
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      ) : null}
      {toggling ? (
        <ConfirmDialog
          title={toggling.is_active ? `Stop offering ${toggling.name} for new assignments?` : `Make ${toggling.name} available again?`}
          consequence={toggling.is_active
            ? "It can no longer be chosen for a new assignment. Employees who already have it keep it, and payroll still deducts it until their assignment ends."
            : "It can be chosen again when assigning a deduction to an employee. Nothing is deducted until it is assigned."}
          confirmLabel={toggling.is_active ? "Make inactive" : "Make available"}
          cancelLabel="Keep as it is"
          tone={toggling.is_active ? "danger" : "default"}
          failureTitle="Not changed"
          onConfirm={confirmToggle}
          onClose={() => setToggling(null)}
        />
      ) : null}
    </>
  );
}

/** Report 55 (OD-34): the government deductions, read only. Apparify works them out from the rules in force. */
const GOVERNMENT_AGENCIES = [
  { agency: "SSS", name: "SSS contribution", what: "Social security. Taken once a month, on the payroll that closes the month." },
  { agency: "PHILHEALTH", name: "PhilHealth contribution", what: "National health insurance. Taken once a month, on the payroll that closes the month." },
  { agency: "PAGIBIG", name: "Pag-IBIG contribution", what: "Home development fund. Taken once a month, on the payroll that closes the month." },
  { agency: "BIR_WHT", name: "Withholding tax", what: "Income tax withheld from each payslip, after the contributions." },
];

function AppLink({ to, children }) {
  const inRouter = useInRouterContext();
  const className = cn("rounded-control font-medium text-accent hover:underline", focusRing);
  return inRouter ? <Link to={to} className={className}>{children}</Link> : <a href={to} className={className}>{children}</a>;
}

function GovernmentTab() {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      setRules(await businessApi.getStatutoryRules());
      setError("");
    } catch (err) {
      setError(plainError(err, "The government rules couldn't be loaded."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error && !rules) return <ErrorState title="Government deductions couldn't be loaded" description={error} onRetry={load} />;
  if (!rules) return <SkeletonRows rows={4} columns={2} label="Loading the government deductions" />;
  const inUse = (agency) => (rules.agencies || []).find((item) => item.agency === agency)?.ruleSets?.find((rule) => rule.status === "in_use") || null;
  return (
    <Surface as="section" aria-label="Government deductions">
      <div className="px-4 py-3">
        <h2 className="text-section-title text-foreground">Government deductions</h2>
        <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">
          Apparify works these out on every payroll from the government rules in force. They cannot be edited here; the employee's share is deducted and the employer's share is shown apart on each payslip.
        </p>
      </div>
      <ul className="divide-y divide-subtle border-t border-subtle">
        {GOVERNMENT_AGENCIES.map((item) => {
          const rule = inUse(item.agency);
          return (
            <li key={item.agency} className="grid gap-2 px-4 py-3 text-body-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:gap-6">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{item.name}</p>
                <p className="text-meta text-secondary">{item.what}</p>
              </div>
              <div className="min-w-0">
                {rule ? (
                  <>
                    <p className="text-foreground">{rule.title}</p>
                    <p className="text-meta text-secondary">{rulePeriodText(rule)}</p>
                    <ul className="mt-1 space-y-0.5 text-meta text-secondary">
                      {ruleFacts(rule).slice(0, 3).map((fact) => <li key={fact.label}>{fact.label}: <span className="text-foreground">{fact.value}</span></li>)}
                    </ul>
                  </>
                ) : <p className="text-secondary">No rule is recorded for today. Payroll stops at this deduction until one is.</p>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="border-t border-subtle px-4 py-3 text-meta text-secondary">
        Sources, tables and verification: <AppLink to="/business/hr/settings/government">HR › Settings › Contributions & tax</AppLink>. Each employee's actual amounts: their payslip, in <AppLink to="/business/hr/payroll">HR › Payroll</AppLink>.
      </p>
    </Surface>
  );
}

/**
 * An employee's name as the members API gives it (`name`, `firstName` / `lastName`); the email only when there is
 * no name at all. The page used to read first_name / last_name, which this API never returns, so every employee was
 * shown by email.
 */
export function memberLabel(member) {
  const named = member?.name && member.name !== "—" ? member.name : "";
  const parts = [member?.firstName ?? member?.first_name, member?.lastName ?? member?.last_name].filter(Boolean).join(" ");
  return named || parts || member?.email || `Member #${member?.id}`;
}

function EmployeeTab({ deductionTypes, members }) {
  const [selectedMember, setSelectedMember] = useState("");
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);

  const assignableMembers = members.filter((member) => ["active", "suspended"].includes(String(member.status || "").toLowerCase()));
  const selectedMemberRecord = assignableMembers.find((member) => String(member.id) === String(selectedMember)) || null;
  const selectedMemberLabel = selectedMemberRecord ? memberLabel(selectedMemberRecord) : "";
  const activeAssignments = deductions.filter((row) => String(row.status || "").toLowerCase() === "active").length;
  const inactiveAssignments = deductions.filter((row) => String(row.status || "").toLowerCase() === "inactive").length;
  const payrollAssignments = deductions.filter((row) => Number(row.affects_payroll || 0) === 1).length;
  const balanceTrackedAssignments = deductions.filter((row) => Number(row.supports_balance_tracking || 0) === 1).length;

  const loadDeductions = useCallback(async () => {
    if (!selectedMember) {
      setDeductions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await businessApi.getEmployeeDeductions(selectedMember);
      setDeductions(response.deductions || []);
    } catch (error) {
      console.error("Failed to load deductions:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedMember]);

  useEffect(() => {
    loadDeductions();
  }, [loadDeductions]);

  const [reviewing, setReviewing] = useState(null);

  const handleCreateAssignment = async (data, type) => setReviewing({ mode: "create", data, type });
  // APP-HR-085: an edit sends only the fields that changed; the review still shows the whole deduction.
  const handleUpdateAssignment = async (data, type, changes) => {
    if (!editingAssignment?.id) return;
    setReviewing({ mode: "update", data, type, id: editingAssignment.id, changes });
  };

  const saveReviewed = async () => {
    if (reviewing.mode === "create") {
      await businessApi.assignDeduction(selectedMember, reviewing.data);
      setShowAssign(false);
    } else {
      await businessApi.updateEmployeeDeduction(reviewing.id, reviewing.changes || {});
      setEditingAssignment(null);
    }
    setReviewing(null);
    await loadDeductions();
  };

  const reviewFacts = (review) => {
    const { data, type } = review;
    // APP-HR-072: a loan with an installment is taken by its installment, whatever amount is stored.
    const amount = type?.supports_balance_tracking && Number(data.installment_amount_cents || 0) > 0
      ? `${formatCurrency(data.installment_amount_cents)} each payroll (the installment), until the balance is paid`
      : type?.calculation_type === "percentage"
      ? `${data.override_rate ?? type?.default_rate ?? 0}% ${data.override_rate != null ? "(their own rate)" : "(the type's default today, kept on their deduction)"}`
      : `${formatCurrency(data.override_amount_cents ?? type?.default_amount_cents ?? 0)} ${data.override_amount_cents != null ? "(their own amount)" : "(the type's default today, kept on their deduction)"}`;
    return [
      { label: "Employee", value: selectedMemberLabel || "This employee" },
      { label: "Deduction", value: type?.name || "Deduction" },
      { label: "Amount", value: amount },
      { label: "Applies", value: `${formatDate(data.effective_from)} to ${data.effective_until ? formatDate(data.effective_until) : "no end date"}` },
      { label: "Status", value: ASSIGNMENT_STATUS_OPTIONS.find((option) => option.value === data.status)?.label || data.status },
      // APP-HR-085: on an edit, a balance or installment is shown only when this save changes it — the form may be older
      // than the loan's real balance, and an unchanged field is not sent.
      ...(data.remaining_balance_cents != null && (review.mode === "create" || (review.changes && "remaining_balance_cents" in review.changes)) ? [{ label: "Balance owed", value: formatCurrency(data.remaining_balance_cents) }] : []),
      ...(data.installment_amount_cents != null && (review.mode === "create" || (review.changes && "installment_amount_cents" in review.changes)) ? [{ label: "Per payroll", value: formatCurrency(data.installment_amount_cents) }] : []),
    ];
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-900/60">
        <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">Employee Assignment Layer</h2>
        <p className="mt-2 max-w-3xl text-sm text-neutral-600 dark:text-neutral-300">Deduction types are business templates. Employee assignments decide whether, when, and how a specific template applies to one employee.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem]">
          <label className="mb-1 block text-sm font-medium dark:text-neutral-300">Employee</label>
          <ShopSelect
            value={selectedMember}
            onValueChange={setSelectedMember}
            options={[
              { value: "", label: "- Select employee -" },
              ...assignableMembers.map((member) => ({ value: member.id, label: memberLabel(member) })),
            ]}
            ariaLabel="Select employee for deduction assignments"
            align="start"
            minPanelWidth={256}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          <p className={helperCls}>Only active or suspended members are shown here. Pre-activation onboarding records stay in the onboarding flow, not direct payroll deduction assignment.</p>
        </div>
        {selectedMember && (
          <button onClick={() => setShowAssign(true)} className="business-primary-action inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Add Assignment</button>
        )}
      </div>

      {selectedMember ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Active</div>
            <div className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{activeAssignments}</div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Inactive</div>
            <div className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{inactiveAssignments}</div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Payroll-affecting</div>
            <div className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{payrollAssignments}</div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Balance-tracked</div>
            <div className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{balanceTrackedAssignments}</div>
          </div>
        </div>
      ) : null}

      {!selectedMember ? (
        <div className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">Select an employee to view their deduction assignments.</div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>
      ) : deductions.length === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">No deduction assignments exist for this employee yet. Add one only when the template should truly apply to this employee.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 dark:border-neutral-800">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/80">
              <tr className="border-b text-left text-xs font-medium uppercase text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
                <th className="px-3 py-2">Deduction Type</th>
                <th className="px-3 py-2">Assignment Terms</th>
                <th className="px-3 py-2">Effective Window</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-neutral-700">
              {deductions.map((deduction) => (
                <tr key={deduction.id} className="align-top hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                  <td className="px-3 py-3">
                    <div className="font-medium dark:text-neutral-100">{deduction.deduction_name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                            <CategoryBadge category={deduction.category} />
                    </div>
                    <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{deduction.deduction_description || "No deduction template description yet."}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="space-y-1 text-sm dark:text-neutral-300">
                      <div><span className="font-medium">Amount:</span> {deduction.supports_balance_tracking && Number(deduction.installment_amount_cents || 0) > 0 ? `${formatCurrency(deduction.installment_amount_cents)} installment` : deduction.calculation_type === "percentage" ? (deduction.override_rate != null ? `${deduction.override_rate}%` : "No rate saved") : formatCurrency(deduction.override_amount_cents ?? 0)} <span className="text-neutral-500 dark:text-neutral-400">(their own; type changes do not alter it)</span></div>
                      <div><span className="font-medium">Type default for new assignments:</span> {deduction.calculation_type === "percentage" ? (deduction.default_rate != null ? `${deduction.default_rate}%` : "No default rate") : formatCurrency(deduction.default_amount_cents)}</div>
                      <div><span className="font-medium">Balance tracking:</span> {deduction.supports_balance_tracking ? `Balance ${formatCurrency(deduction.remaining_balance_cents)} / Installment ${formatCurrency(deduction.installment_amount_cents)}` : "Not applicable"}</div>
                      <div><span className="font-medium">Notes:</span> {deduction.notes || "-"}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm dark:text-neutral-300">
                    <div><span className="font-medium">From:</span> {formatDate(deduction.effective_from)}</div>
                    <div className="mt-1"><span className="font-medium">Until:</span> {formatDate(deduction.effective_until)}</div>
                  </td>
                  <td className="px-3 py-3"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ASSIGNMENT_STATUS_STYLES[deduction.status] || ASSIGNMENT_STATUS_STYLES.active}`}>{formatTokenLabel(deduction.status)}</span></td>
                  <td className="px-3 py-3 text-right"><button onClick={() => setEditingAssignment(deduction)} className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-blue-600 dark:hover:bg-neutral-700 dark:hover:text-blue-400" title="Edit assignment"><Edit2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAssign ? <AssignmentModal open onClose={() => setShowAssign(false)} onSubmit={handleCreateAssignment} deductionTypes={deductionTypes} memberLabel={selectedMemberLabel} /> : null}
      {editingAssignment ? <AssignmentModal open onClose={() => setEditingAssignment(null)} onSubmit={handleUpdateAssignment} deductionTypes={deductionTypes} initial={editingAssignment} memberLabel={selectedMemberLabel} /> : null}
      {reviewing ? (
        <ConfirmDialog
          size="md"
          title={reviewing.mode === "create"
            ? `Deduct ${reviewing.type?.name || "this"} from ${selectedMemberLabel || "this employee"}'s pay?`
            : `Save the change to ${selectedMemberLabel || "this employee"}'s ${reviewing.type?.name || "deduction"}?`}
          facts={reviewFacts(reviewing)}
          consequence={reviewing.data.status === "active" && reviewing.type?.affects_payroll !== 0
            ? "Payroll calculated from the start date deducts this from their pay. Payroll already with Finance or released is not changed."
            : "Payroll does not deduct this while it is not active. Payroll already with Finance or released is not changed."}
          confirmLabel={reviewing.mode === "create" ? "Assign deduction" : "Save deduction"}
          cancelLabel="Back to editing"
          failureTitle="Not saved"
          onConfirm={saveReviewed}
          onClose={() => setReviewing(null)}
        />
      ) : null}
    </div>
  );
}

export default function HRDeductions() {
  const { section } = useParams();
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesResponse, membersResponse] = await Promise.all([businessApi.getDeductionTypes(), businessApi.getMembers()]);
      setDeductionTypes(typesResponse.deductionTypes || []);
      setMembers(membersResponse.members || membersResponse.data || []);
      setError("");
    } catch (err) {
      setError(plainError(err, "The deductions couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadTypes = async () => {
    const response = await businessApi.getDeductionTypes();
    setDeductionTypes(response.deductionTypes || []);
  };

  if (!section || !DEDUCTION_TABS.some((item) => item.key === section)) return <Navigate to={`${BASE}/types`} replace />;
  const tab = section;

  return (
    <HRPageShell title="Deductions" subtitle="What is taken from employees' pay: government deductions Apparify works out, and the business's own deductions and loans.">
      <RouteTabs label="Deductions" className="mb-4" items={DEDUCTION_TABS.map((item) => ({ to: `${BASE}/${item.key}`, label: item.label }))} />
      {tab === "government" ? <GovernmentTab /> : loading ? (
        <SkeletonRows rows={4} columns={4} label="Loading the deductions" />
      ) : error ? (
        <ErrorState title="Deductions couldn't be loaded" description={error} onRetry={load} />
      ) : tab === "types" ? (
        <TypesTab deductionTypes={deductionTypes} onReload={reloadTypes} />
      ) : (
        <EmployeeTab deductionTypes={deductionTypes} members={members} />
      )}
    </HRPageShell>
  );
}
